'use client';

import { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, Search, Check, Globe } from 'lucide-react';
import { SUPPORTED_CURRENCIES, getCurrencyInfo } from '@/config/currencies';
import useBookingStore from '@/store/useBookingStore';
import { cn } from '@/lib/utils';

export default function CurrencySelector({ className }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedCode = useBookingStore((state) => state.currency || 'USD');
  const setCurrency = useBookingStore((state) => state.setCurrency);

  const selectedCurrency = useMemo(
    () => getCurrencyInfo(selectedCode),
    [selectedCode]
  );

  const filteredCurrencies = useMemo(() => {
    if (!query || !query.trim()) return SUPPORTED_CURRENCIES;
    const q = query.toLowerCase().trim();
    return SUPPORTED_CURRENCIES.filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.symbol.toLowerCase().includes(q)
    );
  }, [query]);

  // Focus search input when popover opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    } else {
      setQuery('');
    }
  }, [open]);

  // Click outside listener to close dropdown cleanly
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code) => {
    setCurrency(code);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={cn('relative inline-block text-left', className)}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="Select currency"
        className={cn(
          'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-all duration-200 cursor-pointer shadow-2xs',
          open
            ? 'border-primary ring-3 ring-primary/10 bg-primary/5 text-primary'
            : 'border-slate-200/90 bg-slate-50/80 hover:bg-slate-50 hover:border-primary/60 hover:text-primary transition-colors text-slate-700'
        )}
      >
        <Globe className="h-4 w-4 text-primary flex-shrink-0" />
        <span>
          {selectedCurrency.code} ({selectedCurrency.symbol})
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-slate-400 transition-transform duration-200',
            open && 'rotate-180 text-primary'
          )}
        />
      </button>

      {/* Floating Dropdown List */}
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1.5 w-64 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl shadow-slate-900/10 animate-in fade-in-50 zoom-in-95">
          {/* Search Input Header */}
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search currency..."
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-3 text-xs font-semibold text-slate-800 outline-none focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/10 transition-all"
            />
          </div>

          {/* Currencies Scroll List */}
          <div className="max-h-[220px] overflow-y-auto scrollbar-none space-y-0.5 pr-0.5">
            {filteredCurrencies.length === 0 ? (
              <div className="py-4 text-center text-xs text-slate-400 font-medium">
                No currency found matching &quot;{query}&quot;
              </div>
            ) : (
              filteredCurrencies.map((c) => {
                const isSelected = c.code === selectedCode;
                return (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => handleSelect(c.code)}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 px-2.5 py-2 rounded-xl text-left text-xs transition-colors cursor-pointer border',
                      isSelected
                        ? 'bg-primary/10 text-primary font-bold border-primary/40'
                        : 'border-transparent hover:bg-slate-50 hover:border-primary/40 hover:text-primary text-slate-700 font-medium'
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-extrabold w-9 shrink-0 text-slate-900">
                        {c.code}
                      </span>
                      <span className="truncate text-slate-600 text-[11.5px]">
                        {c.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="font-bold text-slate-400 text-[11px]">
                        {c.symbol}
                      </span>
                      {isSelected && (
                        <Check className="h-3.5 w-3.5 text-primary shrink-0" strokeWidth={2.5} />
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
